import { useState, useEffect, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PRODUCT } from "@/config/brand";
import { Menu, X, LogOut, Shield, LayoutGrid, ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useAccessGate } from "@/hooks/useAccessGate";
import { AccessRequiredDialog } from "@/components/access/AccessRequiredDialog";
import { cn } from "@/lib/utils";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  extraActions?: ReactNode;
  centerContent?: ReactNode;
}

type NavItem = { label: string; href: string; children?: { label: string; href: string }[] };

const navItems: NavItem[] = [
  {
    label: "Spine",
    href: "/spine",
    children: [
      { label: "Pharmalify Spine overview", href: "/spine" },
      { label: "Platform architecture", href: "/platform" },
      { label: "Licensing & Validation", href: "/licensing" },
    ],
  },
  { label: "Solutions", href: "/solutions" },
  { label: "AI Governance", href: "/ai-agents" },
  { label: "Regulatory Watch", href: "/regulatory-hub" },
  { label: "Trust & Validation", href: "/security-compliance" },
  { label: "Company", href: "/about" },
];


const Header = ({ extraActions, centerContent }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const { user, loading, signOut } = useAuth();
  const { isAdmin } = useUserRole();
  const { hasAccess } = useAccessGate();
  const location = useLocation();
  const isLandingPage = location.pathname === "/";

  const handleWorkspaceClick = (e: React.MouseEvent) => {
    if (!hasAccess) {
      e.preventDefault();
      setMobileMenuOpen(false);
      setAccessDialogOpen(true);
    }
  };

  // Disable transitions for the first 50ms after initial mount only — avoids
  // flicker on first paint without forcing a remount on every route change
  // (which caused layout shift and killed transition memory).
  useEffect(() => {
    const t = setTimeout(() => setHasMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setMobileMenuOpen(false);
  };

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50",
      hasMounted ? "transition-[background-color,border-color,box-shadow] duration-500" : "transition-none",
      isLandingPage && !scrolled
        ? "bg-transparent border-b border-transparent"
        : "hero-gradient md:backdrop-blur-md border-b border-primary-foreground/10 shadow-lg"
    )}>
      <div className="container bg-transparent border-transparent">
        <nav className={cn(
          "flex items-center justify-between md:h-24",
          hasMounted ? "transition-[height] duration-300" : "transition-none",
          scrolled ? "h-12" : "h-16"
        )}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-0 group">
            <img
              alt="Pharmalify logo mark"
              className={cn(
                "w-auto drop-shadow-sm object-contain md:h-24",
                hasMounted ? "transition-[height] duration-300" : "transition-none",
                scrolled ? "h-9" : "h-12"
              )}
              src="/lovable-uploads/pharmalify-logo-icon.png"
              width={96}
              height={96}
              fetchPriority="high"
              loading="eager"
              decoding="async" />
            
            <img
              alt="Pharmalify logotype"
              className={cn(
                "w-auto -ml-1 md:-ml-4 drop-shadow-sm object-contain brightness-0 invert md:h-36",
                hasMounted ? "transition-[height] duration-300" : "transition-none",
                scrolled ? "h-12" : "h-16"
              )}
              src="/lovable-uploads/8ea960eb-6630-4ccd-85a0-eba69032adef.svg"
              width={144}
              height={144}
              fetchPriority="high"
              loading="eager"
              decoding="async" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-7">
            {centerContent ? (
              <div className="flex items-center">{centerContent}</div>
            ) : (
              navItems.map((item) =>
                item.children ? (
                  <DropdownMenu key={item.label}>
                    <DropdownMenuTrigger className="text-primary-foreground/80 hover:text-primary-foreground text-sm font-medium transition-colors duration-200 inline-flex items-center gap-1 outline-none">
                      {item.label}
                      <ChevronDown className="h-3.5 w-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[180px]">
                      {item.children.map((child) => (
                        <DropdownMenuItem key={child.href} asChild>
                          <Link to={child.href}>{child.label}</Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="text-primary-foreground/80 hover:text-primary-foreground text-sm font-medium transition-colors duration-200">
                    {item.label}
                  </Link>
                )
              )
            )}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-4 ml-6">
            {extraActions}
            {user && <NotificationBell variant="header" />}
            {user && (
              <Link to="/my-workspace" onClick={handleWorkspaceClick}>
                <Button variant="ghost" className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 gap-2 border border-accent/30">
                  <LayoutGrid className="h-4 w-4 text-accent" />
                  My Workspace
                </Button>
              </Link>
            )}
            {isAdmin && user && (
              <Link to="/admin/tools">
                <Button variant="ghost" className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 gap-2 border border-accent/30">
                  <Shield className="h-4 w-4 text-accent" />
                  Admin
                </Button>
              </Link>
            )}
            {loading ?
            <Button variant="ghost" disabled className="text-primary-foreground/50">
                Loading...
              </Button> :
            user ?
            <Button
              variant="ghost"
              onClick={handleSignOut}
              className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 gap-2">
              
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button> :

            <>
              <Link to="/auth" className="text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
                Customer sign-in
              </Link>
              <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
                <Link to="/contact">{PRODUCT.cta.primary}</Link>
              </Button>
            </>
            }
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu">
            
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="lg:hidden py-4 border-t border-primary-foreground/10 animate-fade-in bg-navy-deep">
            <div className="flex flex-col gap-2">
              {navItems.map((item) =>
                item.children ? (
                  <div key={item.label} className="flex flex-col">
                    <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-primary-foreground/50">
                      {item.label}
                    </div>
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 px-6 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200"
                        onClick={() => setMobileMenuOpen(false)}>
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200"
                    onClick={() => setMobileMenuOpen(false)}>
                    {item.label}
                  </Link>
                )
              )}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-primary-foreground/10 px-4">
                {user && (
                  <Link
                    to="/my-workspace"
                    onClick={(e) => {
                      handleWorkspaceClick(e);
                      if (hasAccess) setMobileMenuOpen(false);
                    }}
                  >
                    <Button variant="outline" className="w-full justify-center border-accent/30 text-accent hover:bg-accent/10 gap-2">
                      <LayoutGrid className="h-4 w-4" />
                      My Workspace
                    </Button>
                  </Link>
                )}
                {isAdmin && user && (
                  <Link to="/admin/tools" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full justify-center border-accent/30 text-accent hover:bg-accent/10 gap-2">
                      <Shield className="h-4 w-4" />
                      Admin
                    </Button>
                  </Link>
                )}
                {loading ?
              <Button variant="outline" disabled className="w-full justify-center border-primary-foreground/20 text-primary-foreground/50">
                    Loading...
                  </Button> :
              user ?
              <Button
                variant="outline"
                onClick={handleSignOut}
                className="w-full justify-center border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 gap-2">
                
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </Button> :

              <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                  variant="outline"
                  className="w-full justify-center border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10">
                  
                      Customer sign-in
                    </Button>
                  </Link>
              }
              {!user && !loading && (
                <Button asChild className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>{PRODUCT.cta.primary}</Link>
                </Button>
              )}
              </div>
            </div>
          </div>
        }
      </div>
      <AccessRequiredDialog
        open={accessDialogOpen}
        onOpenChange={setAccessDialogOpen}
        area="My Workspace"
      />
    </header>);

};

export default Header;